import { useState, useCallback, useMemo } from 'react'
import { BookOpen, BookMarked, BookText, Star, Search, X, ArrowRight, Copy, Check, Info, Loader2 } from 'lucide-react'
import { cn } from '../lib/utils'

interface CrossReferenceProps {
  onClose: () => void
  onInsertText: (text: string) => void
}

type ScriptureSource = 'quran' | 'bible' | 'torah' | 'hadith'

interface SourceConfig {
  id: ScriptureSource
  label: string
  icon: typeof BookOpen
  color: string
}

const SOURCES: SourceConfig[] = [
  { id: 'quran', label: 'Qur\'an', icon: BookOpen, color: 'hsl(var(--accent))' },
  { id: 'bible', label: 'Bible', icon: BookMarked, color: '#4a90d9' },
  { id: 'torah', label: 'Torah', icon: BookText, color: '#c9a84c' },
  { id: 'hadith', label: 'Hadith', icon: BookText, color: '#7c5cbf' },
]

const TOPICS = [
  { topic: 'Tawhid (Oneness of God)', sources: ['quran', 'bible', 'torah'] as ScriptureSource[] },
  { topic: 'Prophets & Messengers', sources: ['quran', 'bible', 'torah'] as ScriptureSource[] },
  { topic: 'Afterlife & Judgment', sources: ['quran', 'bible', 'torah', 'hadith'] as ScriptureSource[] },
  { topic: 'Prayer & Worship', sources: ['quran', 'bible', 'torah', 'hadith'] as ScriptureSource[] },
  { topic: 'Charity & Giving', sources: ['quran', 'bible', 'torah', 'hadith'] as ScriptureSource[] },
  { topic: 'Justice & Mercy', sources: ['quran', 'bible', 'torah'] as ScriptureSource[] },
  { topic: 'Creation', sources: ['quran', 'bible', 'torah'] as ScriptureSource[] },
  { topic: 'Jesus (Isa)', sources: ['quran', 'bible', 'hadith'] as ScriptureSource[] },
  { topic: 'Abraham (Ibrahim)', sources: ['quran', 'bible', 'torah'] as ScriptureSource[] },
  { topic: 'Moses (Musa)', sources: ['quran', 'bible', 'torah'] as ScriptureSource[] },
  { topic: 'Mary (Maryam)', sources: ['quran', 'bible', 'hadith'] as ScriptureSource[] },
  { topic: 'Forgiveness', sources: ['quran', 'bible', 'torah', 'hadith'] as ScriptureSource[] },
  { topic: 'Marriage & Family', sources: ['quran', 'bible', 'torah'] as ScriptureSource[] },
  { topic: 'Angels (Mala\'ika)', sources: ['quran', 'bible', 'hadith'] as ScriptureSource[] },
  { topic: 'Free Will & Destiny', sources: ['quran', 'bible', 'hadith'] as ScriptureSource[] },
]

interface CompareEntry {
  source: ScriptureSource
  reference: string
  text: string
}

const DEMO_COMPARISONS: Record<string, CompareEntry[]> = {
  'Tawhid (Oneness of God)': [
    { source: 'quran', reference: 'Qur\'an 112:1-4', text: 'Say, "He is Allah, [who is] One. Allah, the Eternal Refuge. He neither begets nor is born, nor is there to Him any equivalent."' },
    { source: 'bible', reference: 'Deuteronomy 6:4', text: 'Hear, O Israel: The LORD our God, the LORD is one.' },
    { source: 'torah', reference: 'Isaiah 45:5', text: 'I am the LORD, and there is no other; apart from me there is no God.' },
  ],
  'Jesus (Isa)': [
    { source: 'quran', reference: 'Qur\'an 4:171', text: 'O People of the Scripture, do not commit excess in your religion or say about Allah except the truth. The Messiah, Jesus, the son of Mary, was but a messenger of Allah and His word which He directed to Mary and a soul [created at a command] from Him.' },
    { source: 'bible', reference: 'John 3:16', text: 'For God so loved the world that he gave his one and only Son, that whoever believes in him shall not perish but have eternal life.' },
    { source: 'hadith', reference: 'Sahih Bukhari 3436', text: 'The Prophet said: "Whoever testifies that none has the right to be worshipped but Allah and that I am His Messenger, and that Jesus is the servant of Allah and His Messenger, and His word which He bestowed upon Mary, and a spirit created by Him, then Allah will enter him into Paradise."'},
  ],
  'Prophets & Messengers': [
    { source: 'quran', reference: 'Qur\'an 2:136', text: 'Say, "We have believed in Allah and what has been revealed to us and what has been revealed to Abraham and Ishmael and Isaac and Jacob and the Descendants and what was given to Moses and Jesus and what was given to the prophets from their Lord. We make no distinction between any of them, and we are Muslims [submitting] to Him."' },
    { source: 'bible', reference: 'Ephesians 2:19-20', text: 'Consequently, you are no longer foreigners and strangers, but fellow citizens with God\'s people and also members of his household, built on the foundation of the apostles and prophets, with Christ Jesus himself as the chief cornerstone.' },
    { source: 'torah', reference: 'Deuteronomy 18:18', text: 'I will raise up for them a prophet like you from among their fellow Israelites, and I will put my words in his mouth. He will tell them everything I command him.' },
  ],
  'Afterlife & Judgment': [
    { source: 'quran', reference: 'Qur\'an 99:6-8', text: 'That Day, the people will depart separated [into categories] to be shown [the result of] their deeds. So whoever does an atom\'s weight of good will see it, and whoever does an atom\'s weight of evil will see it.' },
    { source: 'bible', reference: 'Revelation 20:12-13', text: 'And I saw the dead, great and small, standing before the throne, and books were opened. Another book was opened, which is the book of life. The dead were judged according to what they had done as recorded in the books.' },
    { source: 'torah', reference: 'Daniel 12:2', text: 'Multitudes who sleep in the dust of the earth will awake: some to everlasting life, others to shame and everlasting contempt.' },
  ],
  'Charity & Giving': [
    { source: 'quran', reference: 'Qur\'an 2:261', text: 'The example of those who spend their wealth in the way of Allah is like a seed [of grain] which grows seven spikes; in each spike is a hundred grains. And Allah multiplies [His reward] for whom He wills.' },
    { source: 'bible', reference: '2 Corinthians 9:7', text: 'Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver.' },
    { source: 'torah', reference: 'Deuteronomy 15:7-8', text: 'If anyone is poor among your fellow Israelites in any of the towns of the land the LORD your God is giving you, do not be hardhearted or tightfisted toward them. Rather, be openhanded and freely lend them whatever they need.' },
  ],
  'Forgiveness': [
    { source: 'quran', reference: 'Qur\'an 39:53', text: 'Say, "O My servants who have transgressed against themselves [by sinning], do not despair of the mercy of Allah. Indeed, Allah forgives all sins. Indeed, it is He who is the Forgiving, the Merciful."' },
    { source: 'bible', reference: 'Matthew 6:14-15', text: 'For if you forgive other people when they sin against you, your heavenly Father will also forgive you. But if you do not forgive others their sins, your Father will not forgive your sins.' },
    { source: 'torah', reference: 'Psalm 103:12', text: 'As far as the east is from the west, so far has he removed our transgressions from us.' },
  ],
  'Prayer & Worship': [
    { source: 'quran', reference: 'Qur\'an 2:238', text: 'Maintain with care the [obligatory] prayers and [in particular] the middle prayer and stand before Allah, devoutly obedient.' },
    { source: 'bible', reference: 'Matthew 6:6', text: 'But when you pray, go into your room, close the door and pray to your Father, who is unseen. Then your Father, who sees what is done in secret, will reward you.' },
    { source: 'hadith', reference: 'Sahih Bukhari 1:1', text: 'I heard Allah\'s Messenger saying: "The deeds are considered by the intentions, and a person will get the reward according to his intention."' },
  ],
}

export function CrossReference({ onClose, onInsertText }: CrossReferenceProps) {
  const [selectedTopic, setSelectedTopic] = useState<string>('Tawhid (Oneness of God)')
  const [searchQuery, setSearchQuery] = useState('')
  const [inserting, setInserting] = useState<string | null>(null)

  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return TOPICS
    const q = searchQuery.toLowerCase()
    return TOPICS.filter(t =>
      t.topic.toLowerCase().includes(q) ||
      DEMO_COMPARISONS[t.topic]?.some(e =>
        e.text.toLowerCase().includes(q) || e.reference.toLowerCase().includes(q)
      )
    )
  }, [searchQuery])

  const currentComparison = DEMO_COMPARISONS[selectedTopic] || []

  const handleCopy = useCallback(async (text: string) => {
    await navigator.clipboard.writeText(text)
  }, [])

  const handleInsert = useCallback((text: string) => {
    onInsertText(text)
    onClose()
  }, [onInsertText, onClose])

  const getSourceIcon = (source: ScriptureSource) => {
    const config = SOURCES.find(s => s.id === source)
    if (!config) return null
    const Icon = config.icon
    return <Icon className="h-3.5 w-3.5" />
  }

  const getSourceLabel = (source: ScriptureSource) => SOURCES.find(s => s.id === source)?.label || source

  const getSourceColor = (source: ScriptureSource) => SOURCES.find(s => s.id === source)?.color || 'hsl(var(--fg-muted))'

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        onClick={e => e.stopPropagation()}
        className="relative flex h-[85vh] w-full max-w-6xl flex-col rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-2xl animate-slide-up overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[hsl(var(--border))] px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--accent-soft))]">
              <Star className="h-4 w-4 text-[hsl(var(--accent))]" />
            </div>
            <div>
              <h2 className="text-h4 text-[hsl(var(--fg))]">Scripture Cross-Reference</h2>
              <p className="text-caption text-[hsl(var(--fg-muted))]">Compare how different scriptures address the same topic</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn btn-ghost btn-icon" title="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left panel - Topics */}
          <div className="w-64 shrink-0 border-r border-[hsl(var(--border))] flex flex-col">
            <div className="p-3 border-b border-[hsl(var(--border))]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--fg-muted))]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Filter topics..."
                  className="input-field pl-8 py-1.5 text-xs"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredTopics.map(({ topic, sources }) => (
                <button
                  key={topic}
                  onClick={() => setSelectedTopic(topic)}
                  className={`w-full text-left px-4 py-2.5 border-b border-[hsl(var(--border))]/50 transition-all ${
                    selectedTopic === topic
                      ? 'bg-[hsl(var(--accent-soft))] text-[hsl(var(--accent))]'
                      : 'text-[hsl(var(--fg))] hover:bg-[hsl(var(--elevated))]'
                  }`}
                >
                  <div className="text-small font-medium truncate">{topic}</div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {sources.map(s => (
                      <span key={s} className="text-caption text-[hsl(var(--fg-muted))]">
                        {getSourceLabel(s)}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
              {filteredTopics.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                  <Search className="h-6 w-6 text-[hsl(var(--fg-muted))] mb-2 opacity-40" />
                  <p className="text-small text-[hsl(var(--fg-muted))]">No topics matching "{searchQuery}"</p>
                </div>
              )}
            </div>
          </div>

          {/* Right panel - Comparison */}
          <div className="flex-1 overflow-y-auto p-5">
            <div className="mb-4">
              <h3 className="text-h3 text-[hsl(var(--fg))]">{selectedTopic}</h3>
              <p className="text-small text-[hsl(var(--fg-muted))]">Comparative views across scriptures</p>
            </div>

            {currentComparison.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Info className="h-8 w-8 text-[hsl(var(--fg-muted))] mb-3 opacity-40" />
                <p className="text-body text-[hsl(var(--fg-muted))]">No comparison data available for this topic yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentComparison.map((entry, i) => (
                  <div
                    key={i}
                    className="group relative rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--elevated))] p-4 transition-all hover:border-[hsl(var(--accent)/0.3)] hover:shadow-lg"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="flex h-6 w-6 items-center justify-center rounded-md"
                          style={{ background: `${getSourceColor(entry.source)}1A`, color: getSourceColor(entry.source) }}
                        >
                          {getSourceIcon(entry.source)}
                        </div>
                        <span className="text-caption font-medium" style={{ color: getSourceColor(entry.source) }}>
                          {getSourceLabel(entry.source)}
                        </span>
                      </div>
                      <span className="text-caption text-[hsl(var(--fg-muted))] bg-[hsl(var(--surface))] px-2 py-0.5 rounded-full border border-[hsl(var(--border))]">
                        {entry.reference}
                      </span>
                    </div>

                    <p className="text-body text-[hsl(var(--fg))] leading-relaxed mb-3">
                      {entry.text}
                    </p>

                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={() => handleCopy(entry.text)}
                        className="btn btn-ghost btn-icon text-caption"
                        title="Copy text"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleInsert(`${entry.reference}: ${entry.text}`)}
                        className="btn btn-ghost btn-icon text-caption"
                        title="Insert into chat"
                      >
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--elevated))]/50 p-4">
              <p className="text-caption text-[hsl(var(--fg-muted))] flex items-center gap-2">
                <Info className="h-3.5 w-3.5 shrink-0" />
                These comparisons show representative verses. The AI scholar can provide deeper analysis and contextual explanation for any topic.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
